<!-- eslint-disable vue/max-attributes-per-line -->
<template>
  <div class="product-row">
    <b-row class="mt-1">
      <b-col
        cols="12"
        md="4"
        lg="3"
        class="mb-1 mb-md-0"
      >
        <div class="product-card shadow-sm">
          <div class="product-card__thumb">
            <b-img
              :src="`${product.product_catalog.logo}`"
              alt="Producto"
              class="product-card__image"
            />
          </div>

          <div class="product-card__body">
            <h3 class="product-card__title mb-25">
              {{ product.product_catalog.name }}
            </h3>

            <b-badge
              v-if="supplierDisplayName"
              variant="light"
              class="supplier-badge mb-50"
            >
              <feather-icon
                icon="MapPinIcon"
                size="12"
                class="mr-25"
              />
              {{ supplierDisplayName }}
            </b-badge>

            <div class="d-flex flex-column text-muted">
              <span class="h5 mb-0">{{ product.serial_number }}</span>
              <span>
                {{ $t('quantity') }}:
                {{ product.quantity }}
                {{ $tc(unitsFilter(product.unit_type), product.quantity) }}
              </span>
            </div>
          </div>
        </div>
      </b-col>

      <b-col>
        <b-row class="detail-card shadow-sm">
          <b-col
            cols="12"
            md="5"
            class="px-2 custom-padding"
          >
            <h5 class="mb-25">{{ $t('description') }}</h5>
            <div class="text-justify text-muted">
              {{ product.product_catalog.description || '—' }}
            </div>
          </b-col>

          <b-col
            cols="12"
            md="7"
            class="d-flex flex-column flex-md-row justify-content-between align-items-start py-1"
          >
            <div class="price-stack align-self-md-end">
              <div class="d-flex flex-column align-items-start align-items-md-end">
                <span class="font-weight-bolder">{{ $t('unitPrice') }}</span>
                <span class="font-weight-bolder">
                  S/IVA $ {{ product.unit_price | money }}
                </span>

                <!-- NEW: last price updated (from stock) -->
                <span
                  v-if="product.last_price_updated_at"
                  class="text-muted text-small d-flex align-items-center mt-25"
                >
                  <feather-icon
                    icon="ClockIcon"
                    size="14"
                    class="mr-25"
                  />
                  {{ timeAgo(product.last_price_updated_at) }}
                </span>
              </div>
            </div>

            <div class="d-flex align-items-center mt-1 mt-md-0 justify-content-end w-100 w-md-auto">
              <div class="mr-1">
                <div class="d-flex align-items-center">
                  <h3 class="m-0 font-weight-bolder mr-1">
                    {{ $t('total') }}:
                  </h3>
                  <span class="font-weight-bolder text-success h3 m-0">
                    $
                    {{
                      (parseFloat(product.unit_price) *
                        parseFloat(product.quantity))
                        | money
                    }}
                  </span>
                </div>

                <div class="text-muted">
                  {{ $t('quantity') }}: {{ product.quantity }}
                  {{ $tc(unitsFilter(product.unit_type), product.quantity) }}
                </div>
              </div>

              <div class="d-flex justify-content-end">
                <div
                  v-if="roundStage === 'firstRound'"
                  class="d-flex flex-column align-items-stretch"
                >
                  <b-badge
                    v-if="product.supplier_has_bid"
                    variant="success"
                    class="mb-1"
                  >
                    <feather-icon icon="CheckIcon" />
                    Precio mejorado
                  </b-badge>

                  <b-button
                    variant="primary"
                    @click="openImpovePriceModal(product)"
                  >
                    {{ $t('improvePrice') }}
                  </b-button>
                </div>

                <b-button
                  v-else-if="roundStage === 'secondRound'"
                  variant="primary"
                >
                  {{ $t('improvePrice') }}
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-col>

      <b-col
        v-if="roundStage == 'secondRound'"
        cols="12"
        lg="2"
        class="px-0 mt-1 mt-lg-0"
      >
        <div
          class="bg-best-price text-center h-100 d-flex align-items-center justify-content-center border rounded shadow ml-0 ml-lg-2 best-price-card"
        >
          <div class="d-flex flex-column font-weight-bolder">
            <span class="text-light">
              {{ $t('unitPrice') }}
            </span>
            <span class="text-light">
              S/IVA $ {{ product.unit_price | money }}
            </span>

            <!-- Optional: also show updated time in best-price column -->
            <span
              v-if="product.last_price_updated_at"
              class="text-light text-small opacity-75 mt-25 d-flex align-items-center justify-content-center"
            >
              <feather-icon
                icon="ClockIcon"
                size="14"
                class="mr-25"
              />
              {{ timeAgo(product.last_price_updated_at) }}
            </span>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-modal
      :id="`improve-price-modal-${product.id}`"
      :title="$t('improvePrice')"
      :ok-title="$t('improvePrice')"
      :cancel-title="$t('cancel')"
      cancel-variant="outline-danger"
      @ok="bidPrice"
    >
      <h3 class="text-center">
        {{ product.product_catalog.name }}
      </h3>

      <div class="text-center">
        <span class="font-weight-bolder">{{ $t('unitPrice') }}</span>
        <span class="font-weight-bolder">S/IVA $ {{ product.unit_price }}</span>
      </div>

      <!-- NEW: show freshness inside modal too (nice UX) -->
      <div
        v-if="product.last_price_updated_at"
        class="text-center text-muted text-small mt-25 d-flex align-items-center justify-content-center"
      >
        <feather-icon icon="ClockIcon" size="14" class="mr-25" />
        {{ timeAgo(product.last_price_updated_at) }}
      </div>

      <div class="text-center">
        {{ $t('quantity') }}: {{ product.quantity }}
        {{ $tc(unitsFilter(product.unit_type), product.quantity) }}
      </div>

      <div class="d-flex justify-content-end align-items-center mr-1">
        <h3 class="m-0 font-weight-bolder mr-1">
          {{ $t('total') }}:
        </h3>
        <span class="font-weight-bolder text-success h3 m-0">
          $
          {{
            (parseFloat(productToImprove.unit_price) *
              parseFloat(product.quantity))
              | money
          }}
        </span>
      </div>

      <b-form-group :label="$t('improvePrice')">
        <b-form-input
          v-model="productToImprove.unit_price"
          autocomplete="off"
          type="text"
          inputmode="decimal"
          :formatter="limitUnitPriceDecimals"
        />
      </b-form-group>

      <hr>
      <p class="text-muted text-small mb-1">
        {{ $t('bidOfferHint') }}
      </p>
      <b-form-group :label="$t('bidDeliveryLabel')">
        <b-form-input
          v-model="productToImprove.offered_delivery_hours"
          type="number"
          min="0"
          inputmode="numeric"
          :placeholder="$t('bidDeliveryPlaceholder')"
        />
      </b-form-group>
      <b-form-group :label="$t('bidFreightLabel')">
        <b-form-input
          v-model="productToImprove.offered_freight"
          type="number"
          min="0"
          inputmode="decimal"
          :placeholder="$t('bidFreightPlaceholder')"
        />
      </b-form-group>
      <b-form-checkbox v-model="productToImprove.offers_credit">
        {{ $t('bidCreditLabel') }}
      </b-form-checkbox>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  props: {
    product: {
      type: Object,
      default() {
        return {}
      },
    },
    roundStage: {
      type: String,
      default: () => '',
    },
  },

  data() {
    return {
      productToImprove: {},
    }
  },

  computed: {
    colSize() {
      if (this.roundStage === 'firstRound') return '9'
      if (this.roundStage === 'secondRound') return '7'
      return ''
    },
    ...mapState(['apiUrl']),
    ...mapGetters('app', ['unitsFilter']),
    supplierDisplayName() {
      return (
        this.product?.supplier_branch_name
      )
    },
  },

  methods: {
    ...mapActions('quotes', ['bidInQuote', 'fetchSupplierBranchQuoteItems']),

    openImpovePriceModal(product) {
      this.productToImprove = {
        ...product,
        offered_delivery_hours: product.offered_delivery_hours ?? '',
        offered_freight: product.offered_freight ?? '',
        offers_credit: product.offers_credit ?? false,
      }
      this.$bvModal.show(`improve-price-modal-${product.id}`)
    },

    buildBidRecord() {
      const record = {
        id: this.productToImprove.id,
        unit_price: this.productToImprove.unit_price,
      }
      const hours = this.productToImprove.offered_delivery_hours
      if (hours !== '' && hours !== null && hours !== undefined && !Number.isNaN(Number(hours))) {
        record.offered_delivery_hours = Number(hours)
      }
      const freight = this.productToImprove.offered_freight
      if (freight !== '' && freight !== null && freight !== undefined && !Number.isNaN(Number(freight))) {
        record.offered_freight = Number(freight)
      }
      if (typeof this.productToImprove.offers_credit === 'boolean') {
        record.offers_credit = this.productToImprove.offers_credit
      }
      return record
    },

    bidPrice() {
      if (!this.validateDecimals()) {
        return
      }
      this.productToImprove.unit_price = this.limitUnitPriceDecimals(this.productToImprove.unit_price)
      this.bidInQuote({
        quoteId: this.$route.params.quoteId,
        supplier_branch_quote_items_records: [this.buildBidRecord()],
      }).then(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('itemBidded'),
            icon: 'EditIcon',
            variant: 'success',
          },
        })

        this.product.unit_price = this.productToImprove.unit_price

        this.fetchSupplierBranchQuoteItems({
          quoteId: this.$route.params.quoteId,
          branchId: this.$route.query.branchId,
        })
        this.$bvModal.hide(`improve-price-modal-${this.productToImprove.id}`)
      })
    },

    validateDecimals() {
      const decimalPart = String(this.productToImprove.unit_price || '').replace(',', '.').split('.')[1]
      if (decimalPart && decimalPart.length > 6) {
        this.quantityValid = false
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Solo se permiten 6 decimales',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return false
      }
      return true
    },
    limitUnitPriceDecimals(value) {
      const stringValue = String(value ?? '').replace(',', '.')
      const [integerPart, ...decimalParts] = stringValue.split('.')
      const sanitizedInteger = integerPart.replace(/\D/g, '')
      const decimalPart = decimalParts.join('').replace(/\D/g, '')

      if (decimalParts.length === 0) return sanitizedInteger

      return `${sanitizedInteger}.${decimalPart.slice(0, 6)}`
    },

    timeAgo(isoDate) {
      if (!isoDate) return ''
      const date = new Date(isoDate)
      if (Number.isNaN(date.getTime())) return ''

      const diffMs = Date.now() - date.getTime()
      const diffMin = Math.floor(diffMs / 60000)
      const diffHrs = Math.floor(diffMin / 60)
      const diffDays = Math.floor(diffHrs / 24)

      if (diffMin < 1) return 'Actualizado hace unos segundos'
      if (diffMin < 60) return `Actualizado hace ${diffMin} min`
      if (diffHrs < 24) return `Actualizado hace ${diffHrs} h`
      if (diffDays < 7) return `Actualizado hace ${diffDays} día${diffDays === 1 ? '' : 's'}`

      return `Actualizado el ${date.toLocaleDateString()}`
    },
  },
}
</script>

<style lang="scss" scoped>
.product-row {
  .product-card {
    background: #ffffff;
    border: 1px solid #e6e6e6;
    border-radius: 12px;
    padding: 12px;
    display: flex;
    gap: 12px;
    align-items: center;
    height: 100%;
  }

  .product-card__thumb {
    width: 72px;
    height: 72px;
    border-radius: 12px;
    background: #f4f5f7;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
  }

  .product-card__image {
    width: 64px;
    height: 64px;
    object-fit: contain;
  }

  .product-card__title {
    font-size: 1.05rem;
    line-height: 1.2;
  }

  .supplier-badge {
    background-color: #eef4ff;
    color: #2d5dd1;
    font-weight: 600;
  }

  .detail-card {
    background: #ffffff;
    border: 1px solid #e6e6e6;
    border-radius: 12px;
    padding: 10px 8px;
    height: 100%;
  }

  .price-stack {
    min-width: 160px;
  }

  .best-price-card {
    border-radius: 12px;
  }
}

.bg-best-price {
  background-color: #77c77b;
}
.custom-padding {
  padding-top: 8px;
  padding-bottom: 8px;
}
.text-small {
  font-size: 10px;
}
</style>
